export type ValuationKey = "RC" | "ACV" | "IACV";
export type PerilKey = "FL1R" | "FL1RV" | "FL2" | "FL3";
export type ReplacementCostPercentage = 60 | 90 | 92 | 95 | 100;

export const NEW_BUSINESS_REQUIRED_LOCATION_FORMS = ["FL373A"];

export function isCompleteYearBuilt(value: unknown) {
  return /^\d{4}$/.test(String(value ?? ""));
}

const VALUATION_OPTIONS: Record<ValuationKey, { value: ValuationKey; text: string }> = {
  RC: { value: "RC", text: "Replacement Cost" },
  ACV: { value: "ACV", text: "Actual Cash Value" },
  IACV: { value: "IACV", text: "Indexed Actual Cash Value" },
};

const REPLACEMENT_COST_PERCENTAGE_OPTIONS: Record<ReplacementCostPercentage, { value: string; text: string }> = {
  60: { value: "60", text: "60%" },
  90: { value: "90", text: "90%" },
  92: { value: "92", text: "92%" },
  95: { value: "95", text: "95%" },
  100: { value: "100", text: "100%" },
};

const OLD_REPLACEMENT_COST_PERCENTAGES: ReplacementCostPercentage[] = [100, 95, 92, 90];
const NEW_REPLACEMENT_COST_PERCENTAGES: ReplacementCostPercentage[] = [100, 90];
const IACV_REPLACEMENT_COST_PERCENTAGES: ReplacementCostPercentage[] = [60];

const OLD_PERIL_VALUATION_MAP: Record<PerilKey, ValuationKey[]> = {
  FL1R: ["ACV"],
  FL1RV: ["ACV"],
  FL2: ["RC", "ACV"],
  FL3: ["RC", "ACV"],
};

const NEW_PERIL_VALUATION_MAP: Record<PerilKey, ValuationKey[]> = {
  FL1R: ["ACV"],
  FL1RV: ["ACV"],
  FL2: ["RC", "ACV", "IACV"],
  FL3: ["RC", "IACV"],
};

export type DeductibleKey = "250" | "500" | "1000" | "2500" | "5000" | "10000";

const DEDUCTIBLE_OPTIONS: Record<DeductibleKey, { value: DeductibleKey; text: string }> = {
  "250": { value: "250", text: "$250" },
  "500": { value: "500", text: "$500" },
  "1000": { value: "1000", text: "$1,000" },
  "2500": { value: "2500", text: "$2,500" },
  "5000": { value: "5000", text: "$5,000" },
  "10000": { value: "10000", text: "$10,000" },
};

const ALL_DEDUCTIBLE_KEYS = Object.keys(DEDUCTIBLE_OPTIONS) as DeductibleKey[];

export function getAllowedDeductibleKeys(useNewBusinessRules = false): DeductibleKey[] {
  if (useNewBusinessRules) {
    return ALL_DEDUCTIBLE_KEYS.filter((key) => key !== "250");
  }

  return ALL_DEDUCTIBLE_KEYS;
}

export function getDeductibleOptions(useNewBusinessRules = false) {
  return getAllowedDeductibleKeys(useNewBusinessRules).map((key) => DEDUCTIBLE_OPTIONS[key]);
}

export function getAllowedValuationKeys(perils?: string, useNewBusinessRules = false): ValuationKey[] {
  const map = useNewBusinessRules ? NEW_PERIL_VALUATION_MAP : OLD_PERIL_VALUATION_MAP;

  return map[perils as PerilKey] ?? (useNewBusinessRules ? ["RC", "ACV", "IACV"] : ["RC", "ACV"]);
}

export function getValuationOptions(perils?: string, useNewBusinessRules = false) {
  return getAllowedValuationKeys(perils, useNewBusinessRules).map((key) => VALUATION_OPTIONS[key]);
}

export function getAllowedReplacementCostPercentages(
  valuation?: string,
  useNewBusinessRules = false
): ReplacementCostPercentage[] {
  if (valuation === "IACV") return IACV_REPLACEMENT_COST_PERCENTAGES;
  if (valuation !== "RC") return [];

  return useNewBusinessRules ? NEW_REPLACEMENT_COST_PERCENTAGES : OLD_REPLACEMENT_COST_PERCENTAGES;
}

export function getReplacementCostPercentageOptions(valuation?: string, useNewBusinessRules = false) {
  return getAllowedReplacementCostPercentages(valuation, useNewBusinessRules).map(
    (percentage) => REPLACEMENT_COST_PERCENTAGE_OPTIONS[percentage]
  );
}

export function isValuationAllowed(
  perils: string | undefined,
  valuation: string | undefined,
  useNewBusinessRules: boolean
) {
  return getAllowedValuationKeys(perils, useNewBusinessRules).includes(valuation as ValuationKey);
}

export function getDefaultValuationForPeril(perils?: string, useNewBusinessRules = false): ValuationKey | undefined {
  if (!useNewBusinessRules) return undefined;
  if (perils === "FL1R" || perils === "FL1RV") return "ACV";
  if (perils === "FL2") return "IACV";
  if (perils === "FL3") return "RC";

  return undefined;
}

export function getFallbackValuationForPeril(perils?: string, useNewBusinessRules = false) {
  if (!useNewBusinessRules && (perils === "FL2" || perils === "FL3")) return "ACV";

  return (
    getDefaultValuationForPeril(perils, useNewBusinessRules) ?? getAllowedValuationKeys(perils, useNewBusinessRules)[0]
  );
}
